import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  LayoutDashboard,
  Network,
  Settings,
  Play,
  Eye,
  Pencil,
  Search,
} from "lucide-react";
import { useTopologies } from "../../api/hooks.js";
import { useWorldStore } from "../../stores/world.js";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: topologies } = useTopologies();
  const { setMode } = useWorldStore();

  useEffect(() => {
    function down(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  function runAction(fn: () => void) {
    fn();
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Dialog */}
      <Command
        className="relative w-full max-w-lg bg-surface-light border border-border-subtle/30 rounded-xl shadow-2xl shadow-black/40 overflow-hidden animate-fade-in"
        label="Command palette"
      >
        <div className="flex items-center gap-3 px-4 border-b border-border-subtle/20">
          <Search className="w-4 h-4 text-text-muted shrink-0" />
          <Command.Input
            placeholder="Search topologies, actions..."
            className="w-full bg-transparent py-3.5 text-sm text-text-primary placeholder-text-muted focus:outline-none"
          />
        </div>

        <Command.List className="max-h-72 overflow-y-auto py-2 px-2">
          <Command.Empty className="py-8 text-center text-sm text-text-muted">
            No results found.
          </Command.Empty>

          <Command.Group
            heading={
              <span className="text-[11px] text-text-muted font-medium px-2">
                Navigation
              </span>
            }
          >
            <CommandItem
              icon={LayoutDashboard}
              label="Dashboard"
              shortcut="Cmd+1"
              onSelect={() => runAction(() => navigate("/"))}
            />
            <CommandItem
              icon={Network}
              label="Topology View"
              shortcut="Cmd+2"
              onSelect={() => runAction(() => navigate("/topology"))}
            />
            <CommandItem
              icon={Settings}
              label="Settings"
              shortcut="Cmd+3"
              onSelect={() => runAction(() => navigate("/settings"))}
            />
          </Command.Group>

          <Command.Group
            heading={
              <span className="text-[11px] text-text-muted font-medium px-2">
                Actions
              </span>
            }
          >
            <CommandItem
              icon={Eye}
              label="Switch to Monitor Mode"
              onSelect={() => runAction(() => setMode("monitor"))}
            />
            <CommandItem
              icon={Pencil}
              label="Switch to Design Mode"
              onSelect={() => runAction(() => setMode("design"))}
            />
          </Command.Group>

          {topologies && topologies.length > 0 && (
            <Command.Group
              heading={
                <span className="text-[11px] text-text-muted font-medium px-2">
                  Topologies
                </span>
              }
            >
              {topologies.map((t) => (
                <CommandItem
                  key={t.id}
                  icon={Network}
                  label={`${t.definition?.type ?? "custom"} (${t.definition?.agents?.length ?? 0} agents)`}
                  subtitle={t.id}
                  onSelect={() => runAction(() => navigate(`/topology/${t.id}`))}
                />
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command>
    </div>
  );
}

function CommandItem({
  icon: Icon,
  label,
  subtitle,
  shortcut,
  onSelect,
}: {
  icon: any;
  label: string;
  subtitle?: string;
  shortcut?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-text-secondary cursor-pointer transition-colors data-[selected=true]:bg-brand-purple/10 data-[selected=true]:text-text-primary"
    >
      <Icon className="w-4 h-4 text-text-muted shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {subtitle && (
        <span className="text-[11px] text-text-muted font-mono truncate max-w-[120px]">
          {subtitle}
        </span>
      )}
      {shortcut && (
        <span className="text-[11px] text-text-muted shrink-0">{shortcut}</span>
      )}
    </Command.Item>
  );
}
